import { AxiosResponse } from "axios";
import { apiErrorWithMessages } from "../lib/apiError";
import useApi from "../services/ApiService";
import { Paging } from "../models/Paging";
import { MigrationFlagStatus } from "../models/Flags/MigrationFlagStatus";
import {FirmDocumentsFolder, FirmDocumentsFolderWithStatistics} from "../models/FirmDocumentFolder/FirmDocumentsFolder";
import {FirmDocument} from "../models/FirmDocumentFolder/FirmDocument";
import { ConflictGroupDto, ConflictResolutionItemDto } from "../models/ConflictGroup";
import { ConflictReasonCount } from "../models/ConflictReasonCount";
import {EmptyFolder} from "../models/EmptyFolder";
import {QUERY_CONFIG} from "../constants/QueryConfig";

export interface GetFirmDocumentsFoldersQueryOptions {
  organizationId?: string;
  status?: string[] | null;
  isLocked?: boolean | null;
  createdBy?: string[];
  folderPath?: string[] | null;
  ids?: string[];
  pageNumber?: number;
  pageSize?: number;
  orderBy?: string;
  descending?: boolean;
  hasFlaggedPaths?: boolean | null;
  hasConflicts?: boolean | null;
  /** Only entities whose flag is in one of these states. */
  flagStatus?: MigrationFlagStatus[] | null;
}
export class GetFirmDocumentsForFirmDocumentsFolderQueryOptions {
  status?: string[] | null;
  extension?: string[] | null;
  pageNumber?: number;
  pageSize?: number;
  orderBy?: string;
  descending?: boolean;
  filePath?: string | null;
  destinationPath?: string | null;
  minFileSize?: number | null;
  maxFileSize?: number | null;
  type?: number | null;
  failureType?: string | null;
  conflictReason?: string[] | null;
  conflictStatus?: string[] | null;
}

export class GetFirmDocumentsFolderEmptyFoldersForFirmDocumentsFolderQueryOptions {
  status?: string[] | null;
  uploadPathFromRoot?: string | null;
  pageNumber?: number;
  pageSize?: number;
  orderBy?: string;
  descending?: boolean;
}

export class FirmDocumentsFoldersRepository {
  private api = useApi();

  async getFirmDocumentsFolders(
    options: GetFirmDocumentsFoldersQueryOptions = {}
  ): Promise<{ data: FirmDocumentsFolder[]; paging: Paging | null }> {
    const {
      status = null,
      isLocked = null,
      createdBy = null,
      folderPath = null,
      ids = null,
      pageNumber = null,
      pageSize = null,
      orderBy = null,
      descending = true,
      hasFlaggedPaths = null,
      hasConflicts = null,
      flagStatus = null,
    } = options;

    try {
      const response = await this.api.get("/api/firmDocumentsFolders", {
        params: {
          Status: status,
          IsLocked: isLocked,
          CreatedBy: createdBy,
          FolderPath: folderPath,
          Id: ids,
          PageNumber: pageNumber,
          PageSize: pageSize,
          OrderBy: orderBy,
          Descending: descending,
          HasFlaggedPaths: hasFlaggedPaths,
          HasConflicts: hasConflicts,
          FlagStatus: flagStatus,
        },
        paramsSerializer: {
          indexes: true,
        },
      });

      const { data, headers } = response;
      let paging: Paging | null = null;
      const paginationHeader = headers["x-pagination"];
      if (paginationHeader) {
        paging = JSON.parse(paginationHeader || "");
      }

      return { data, paging };
    } catch (error) {
      throw new Error("Failed to fetch firm documents folders");
    }
  }

  async getFirmDocumentsFolder(
    id: string
  ): Promise<FirmDocumentsFolderWithStatistics> {
    try {
      const response = await this.api.get(`/api/firmDocumentsFolders/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch firm documents folder with id: ${id}`);
    }
  }

  async getFirmDocumentsForFirmDocumentsFolder(
    firmDocumentsFolderId?: string,
    options: GetFirmDocumentsForFirmDocumentsFolderQueryOptions = {}
  ): Promise<{ data: FirmDocument[]; paging: Paging | null }> {
    const {
      status = null,
      extension = null,
      pageNumber = null,
      pageSize = null,
      orderBy = null,
      descending = true,
      filePath = null,
      destinationPath = null,
      minFileSize = null,
      maxFileSize = null,
      type = null,
      failureType = null,
      conflictReason = null,
      conflictStatus = null
    } = options;
    try {
      const apiUrl = firmDocumentsFolderId
        ? `/api/FirmDocumentsFolders/${firmDocumentsFolderId}/files`
        : "/api/FirmDocuments";
      const response = await this.api.get(apiUrl, {
        params: {
          Status: status,
          Extension: extension,
          FilePath: filePath,
          DestinationPath: destinationPath,
          MinFileSize: minFileSize,
          MaxFileSize: maxFileSize,
          PageNumber: pageNumber,
          PageSize: pageSize,
          OrderBy: orderBy,
          Descending: descending,
          Type: type,
          FailureType: failureType,
          ConflictReason: conflictReason,
          ConflictStatus: conflictStatus,
        },
        paramsSerializer: {
          indexes: true,
        },
        timeout: QUERY_CONFIG.DEFAULT_TIMEOUT,
      });
      const { data, headers } = response;
      let paging: Paging | null = null;
      const paginationHeader = headers["x-pagination"];
      if (paginationHeader)
        paging = JSON.parse(paginationHeader || "");
      
      return { data, paging };
    } catch (error) {
      const message = firmDocumentsFolderId
        ? `Failed to fetch files for firm documents folder with id: ${firmDocumentsFolderId}`
        : `Failed to fetch firm documents`;
      throw new Error(message);
    }
  }

  async getFirmDocumentsFolderEmptyFoldersForFirmDocumentsFolder(
    id: string,
    options: GetFirmDocumentsFolderEmptyFoldersForFirmDocumentsFolderQueryOptions = {}
  ): Promise<{ data: EmptyFolder[]; paging: Paging | null }> {
    const {
      status = null,
      uploadPathFromRoot = null,
      pageNumber = null,
      pageSize = null,
      orderBy = null,
      descending = true,
    } = options;
    try {
      const response = await this.api.get(`/api/FirmDocumentsFolders/${id}/emptyFolders`, {
        params: {
          Status: status,
          UploadPathFromRoot: uploadPathFromRoot,
          PageNumber: pageNumber,
          PageSize: pageSize,
          OrderBy: orderBy,
          Descending: descending,
        },
        paramsSerializer: {
          indexes: true,
        },
        timeout: 100 * 1000,
      });

      const { data, headers } = response;
      let paging: Paging | null = null;
      const paginationHeader = headers["x-pagination"];
      if (paginationHeader)
        paging = JSON.parse(paginationHeader || "");

      return { data, paging };
    } catch (error) {
      // Handle error
      throw new Error(`Failed to fetch empty folder for intake with id: ${id}`);
    }
  }

  async updateFirmDocumentsFolderFlag(
    firmDocumentsFolderId: string,
    flagStatus: MigrationFlagStatus
  ): Promise<void> {
    try {
      const data: Record<string, any> = {
        firmDocumentsFolderId: firmDocumentsFolderId,
        flagStatus: flagStatus,
      };

      const response: AxiosResponse<void> = await this.api.patch(
        `/api/firmDocumentsFolders/updateFlag`,
        data
      );

      return response.data;
    } catch (error) {
      throw new Error(`Failed to update flag info of firm documents folder with id: ${firmDocumentsFolderId}`);
    }
  }

  async updateFirmDocumentsFolderBlock(
    firmDocumentsFolderId: string, 
    block: boolean
  ): Promise<void> {
    try {
      const data: Record<string, any> = {
        entityId: firmDocumentsFolderId,
        block: block,
      };

      const response: AxiosResponse<void> = await this.api.patch(
        `/api/firmDocumentsFolders/updateBlock`,
        data
      );

      return response.data;
    } catch (error) {
      throw new Error(`Failed to update block info of firm documents folder with id: ${firmDocumentsFolderId}`);
    }
  }

  async markNotFoundFileForRetry(
    firmDocumentsFolderId: string,
    firmDocumentId: string,
    reason: string
  ): Promise<void> {
    try {
      const data: Record<string, any> = {
        migrationEntityId: firmDocumentsFolderId,
        fileId: firmDocumentId,
        reason: reason
      };
      await this.api.post(
        `/api/firmDocuments/markNotFoundForRetry`,
        data
      );
    } catch (error) {
      throw new Error(`Failed to mark not found file for retry for firmDocumentsFolderId: ${firmDocumentsFolderId} and firmDocumentId: ${firmDocumentId}`);
    }
  }

  async getConflictGroups(firmDocumentsFolderId: string, includeResolved = false): Promise<ConflictGroupDto[]> {
    try {
      const response = await this.api.get(`/api/FirmDocuments/GetConflictGroups`, {
        params: { firmDocumentsFolderId: firmDocumentsFolderId, includeResolved: includeResolved },
      });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch conflict groups for firm documents folder with id: ${firmDocumentsFolderId}`);
    }
  }

  async getConflictReasonCounts(): Promise<ConflictReasonCount[]> {
    try {
      const response = await this.api.get(`/api/FirmDocuments/GetConflictReasonCounts`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch conflict reason counts for firm document folders`);
    }
  }

  async resolveConflictGroup(resolutions: ConflictResolutionItemDto[]): Promise<void> {
    try {
      await this.api.post(`/api/FirmDocuments/ResolveConflictGroup`, {
        resolutions: resolutions,
      });
    } catch (error) {
      throw apiErrorWithMessages(error, "Failed to resolve conflict group");
    }
  }
}

export default FirmDocumentsFoldersRepository;
